// SourcePanel: manual URL input + advanced headers + virtual browser trigger
// + detected-stream list.

import { useState } from "react";
import type { DetectedStream } from "@shared/protocol";
import type { RoomActions, RoomState } from "../socket/useRoomSocket.js";

export interface SourcePanelProps {
  state: RoomState;
  actions: RoomActions;
}

export function SourcePanel({ state, actions }: SourcePanelProps): JSX.Element {
  const [url, setUrl] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [referer, setReferer] = useState("");
  const [cookie, setCookie] = useState("");
  const [userAgent, setUserAgent] = useState("");
  const [origin, setOrigin] = useState("");
  const [authorization, setAuthorization] = useState("");

  const submit = () => {
    const trimmed = url.trim();
    if (!trimmed) return;
    const headers: Record<string, string | undefined> = {};
    if (referer.trim()) headers.referer = referer.trim();
    if (cookie.trim()) headers.cookie = cookie.trim();
    if (userAgent.trim()) headers.userAgent = userAgent.trim();
    if (origin.trim()) headers.origin = origin.trim();
    if (authorization.trim()) headers.authorization = authorization.trim();
    const anyHeaders = Object.values(headers).some((v) => v && v.length > 0);
    actions.loadSource({
      kind: "url",
      url: trimmed,
      ...(anyHeaders ? { headers } : {})
    });
  };

  const openBrowser = () => {
    actions.browserOpen();
  };

  return (
    <section className="panel source">
      <h3>Source</h3>
      <div className="row">
        <input
          type="url"
          placeholder="https://example.com/stream.m3u8"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && submit()}
        />
        <button onClick={submit} disabled={!url.trim()}>Load</button>
      </div>
      <button className="link" onClick={() => setShowAdvanced((s) => !s)}>
        {showAdvanced ? "▾" : "▸"} Advanced headers
      </button>
      {showAdvanced && (
        <div className="advanced">
          <label>
            Referer
            <input value={referer} onChange={(e) => setReferer(e.target.value)} placeholder="https://example.com/watch" />
          </label>
          <label>
            Cookie
            <input value={cookie} onChange={(e) => setCookie(e.target.value)} placeholder="session=..." />
          </label>
          <label>
            User-Agent
            <input value={userAgent} onChange={(e) => setUserAgent(e.target.value)} placeholder="Mozilla/5.0 ..." />
          </label>
          <label>
            Origin
            <input value={origin} onChange={(e) => setOrigin(e.target.value)} placeholder="https://example.com" />
          </label>
          <label>
            Authorization
            <input value={authorization} onChange={(e) => setAuthorization(e.target.value)} placeholder="Bearer ..." />
          </label>
          <p className="hint">
            Headers stay in server memory and are forwarded to the origin. Use HTTPS only.
          </p>
        </div>
      )}
      <div className="browser-button">
        <button onClick={openBrowser} className="primary">
          Open Virtual Browser
        </button>
        <span className="hint">
          Sniffs HLS streams on the target site. The server proxies what you pick.
        </span>
      </div>
      {state.browser.status !== "closed" && state.detectedStreams.length > 0 && (
        <div className="streams">
          <h4>Detected streams</h4>
          <ul>
            {state.detectedStreams.map((s) => (
              <StreamCard
                key={s.id}
                stream={s}
                onUse={() => actions.loadSource({ kind: "browserCandidate", browserSessionId: state.browser.sessionId ?? "", candidateId: s.id })}
              />
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

function StreamCard({ stream, onUse }: { stream: DetectedStream; onUse: () => void }): JSX.Element {
  return (
    <li className="stream-card">
      <div className="meta">
        <div className="kind">{stream.kind}</div>
        <div className="url" title={stream.displayUrl}>{stream.displayUrl}</div>
        {stream.resolutions.length > 0 && (
          <div className="resolutions">
            {stream.resolutions.map((r, i) => (
              <span key={i} className="badge">{r.width}×{r.height}{r.bandwidth ? ` · ${Math.round(r.bandwidth / 1000)}kbps` : ""}</span>
            ))}
          </div>
        )}
        <div className="stats">
          {stream.duration !== null && <span>Duration: {fmtDuration(stream.duration)}</span>}
          {stream.liveWindowDuration !== null && <span>Live window: {stream.liveWindowDuration.toFixed(1)}s</span>}
          {stream.byteLength !== null && <span>{stream.byteLength} bytes</span>}
          {stream.mimeType && <span>{stream.mimeType}</span>}
        </div>
      </div>
      <button onClick={onUse}>Use stream</button>
    </li>
  );
}

function fmtDuration(sec: number): string {
  const total = Math.floor(sec);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}